import { useMemo, useState } from "react";

type Props = {
  cues: string[];
  /** Opens the follow-up draft flow for this cue line. */
  onUseForFollowUp: (line: string) => void;
};

/**
 * Transcript-derived date/time cues: short preview, tap to expand full text, dismiss, or start follow-up.
 */
export function TranscriptCueList(props: Props) {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [expandedLine, setExpandedLine] = useState<string | null>(null);

  const visible = useMemo(() => props.cues.filter((line) => !dismissed.includes(line)), [props.cues, dismissed]);

  function toggleExpand(line: string) {
    setExpandedLine((prev) => (prev === line ? null : line));
  }

  function dismiss(line: string) {
    setDismissed((d) => (d.includes(line) ? d : [...d, line]));
    setExpandedLine((ex) => (ex === line ? null : ex));
  }

  if (!props.cues.length) {
    return <p className="mt-1 text-xs text-gray-500">No obvious date/time phrases detected yet.</p>;
  }

  if (!visible.length) {
    return (
      <p className="mt-1 text-xs text-gray-500 italic">
        All cues dismissed for now. They can reappear if the transcript is updated or reprocessed.
      </p>
    );
  }

  return (
    <ul className="mt-1 space-y-1.5 text-xs">
      {visible.map((line, idx) => {
        const isOpen = expandedLine === line;
        return (
          <li key={`${idx}-${line.slice(0, 48)}`} className="rounded-lg border border-indigo-100/80 bg-white/90 overflow-hidden dark:border-indigo-800/60 dark:bg-zinc-900/80">
            <button
              type="button"
              className="w-full text-left px-2 py-1.5 hover:bg-indigo-50/90 dark:hover:bg-indigo-950/40 text-gray-800 dark:text-zinc-200"
              onClick={() => toggleExpand(line)}
              aria-expanded={isOpen}
            >
              <span className={isOpen ? "block whitespace-pre-wrap break-words" : "block line-clamp-3"}>{line}</span>
            </button>
            {isOpen ? (
              <div className="flex flex-wrap gap-2 border-t border-indigo-100 dark:border-indigo-800/60 px-2 py-2 bg-indigo-50/40 dark:bg-indigo-950/30">
                <button
                  type="button"
                  className="rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-medium text-red-800 hover:bg-red-50 dark:border-red-800 dark:bg-zinc-900 dark:text-red-200 dark:hover:bg-red-950/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss(line);
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onUseForFollowUp(line);
                  }}
                >
                  Set up follow-up
                </button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
